import { api } from './api';

// Card images shown are counted in batches (every 10 s, and when the page is left).
const MAX_BATCH = 500;
let imageViewsToTrack = 0;
let trackingTimeout: number | null = null;

const sendImageViewsBatch = (leavingPage = false) => {
  if (trackingTimeout) {
    clearTimeout(trackingTimeout);
    trackingTimeout = null;
  }
  const count = Math.min(imageViewsToTrack, MAX_BATCH);
  imageViewsToTrack = 0;
  if (count <= 0) return;

  if (leavingPage) {
    // keepalive lets the request outlive the page, with the same credentials as axios.
    const token = localStorage.getItem('access_token');
    void fetch(`${api.defaults.baseURL}/metrics/image-views`, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ count }),
    }).catch(() => {});
    return;
  }
  api.post('/metrics/image-views', { count }).catch(() => {
    // Metrics are best effort.
  });
};

export type BackfillStep = 'searchNames' | 'imageBases' | 'imageProbe' | 'imageQueue' | 'dexIds';

/** Catalog upgrade (GET /admin/sync/tasks). */
export interface BackfillProgress {
  running: boolean;
  step?: BackfillStep;
  startedAt?: string;
  finishedAt?: string;
  searchNamesUpdated: number;
  setsTotal: number;
  setsDone: number;
  setsFailed: string[];
  cardsUpdated: number;
  probeTotal: number;
  probeDone: number;
  probeFound: number;
  cardsToQueue: number;
  cardsQueued: number;
  imagesQueued: number;
  /** Waiting or failed downloads whose address was outdated, queued again. */
  imagesRequeued: number;
  /** Waiting or failed downloads for a language without artwork, removed. */
  imagesDropped: number;
  dexIdsTotal: number;
  dexIdsChecked: number;
  dexIdsUpdated: number;
}

/** Background TCGdex import (GET /admin/sync/tasks). */
export interface ImportProgress {
  running: boolean;
  startedAt?: string;
  finishedAt?: string;
  langs: string[];
  setsTotal: number;
  setsDone: number;
  currentSet?: { id: string; cardsDone: number; cardsTotal: number };
  cardsProcessed: number;
  cardsCreated: number;
  cardsUpdated: number;
  cardsNotFound: number;
  cardsFailed: number;
  imagesRegistered: number;
  errors: string[];
  errorCount: number;
  fatalError?: string;
}

/** Refresh of the Cardmarket prices of owned cards (GET /admin/sync/tasks). */
export interface PriceRefreshProgress {
  enabled: boolean;
  running: boolean;
  startedAt?: string;
  finishedAt?: string;
  /** Owned cards whose price was more than a day old, looked up in this run. */
  cardsTotal: number;
  cardsDone: number;
  priced: number;
  /** Cardmarket has no price for them. */
  noPrice: number;
  /** TCGdex did not answer: retried next run. */
  failed: number;
}

export interface SyncTasks {
  import: ImportProgress;
  backfill: BackfillProgress;
  downloads: ImageJobStatus;
  prices: PriceRefreshProgress;
}

/** One set in GET /admin/sync/overview. */
export interface SetOverview {
  setId: string;
  name: string;
  releaseDate?: string;
  cards: number;
  withImage: number;
  french: number;
  englishOnly: number;
  missing: number;
  unchecked: number;
  expectedImages: number;
  downloaded: number;
  pending: number;
  failed: number;
  abandoned: number;
}

/** notFound: no file at the address; unavailable: no answer, worth retrying; other: storage or unknown. */
export type FailureCause = 'notFound' | 'unavailable' | 'other';

export interface CatalogOverview {
  generatedAt: string;
  totals: Omit<SetOverview, 'setId' | 'name' | 'releaseDate'> & {
    sets: number;
    storedBytes: number;
  };
  sets: SetOverview[];
  /** Failed downloads by cause, most frequent first, with their most frequent message. */
  failures: Array<{ cause: FailureCause; count: number; sample: string }>;
}

export interface GlobalStats {
  totalUsers: number;
  newUsersThisWeek: number;
  totalCards: number;
  newCardsThisWeek: number;
  totalValue: number;
}

export interface TopCard {
  cardId: string;
  name: string;
  setName: string;
  imageUrl?: string;
  totalQuantity: number;
  ownersCount: number;
}

export interface TopUser {
  userId: string;
  email: string;
  pseudo: string;
  totalValue: number;
  cardsCount: number;
}

export interface ChartData {
  newUsersByDay: { date: string; count: number }[];
  newCardsByDay: { date: string; count: number }[];
}

export interface SetDistribution {
  setName: string;
  cardsCount: number;
  uniqueCards: number;
  setLogo?: string;
}

export interface TopExpensiveCard {
  cardId: string;
  name: string;
  setName: string;
  imageUrl: string;
  unitPrice: number;
  ownerPseudo: string;
}

export interface TopExpensiveGradedCard {
  cardId: string;
  name: string;
  setName: string;
  imageUrl: string;
  unitPrice: number;
  ownerPseudo: string;
  gradingCompany?: string;
  gradingScore?: string | number;
}

// ────────────────────────────────────────────────────────────
// API Request Tracking Types
// ────────────────────────────────────────────────────────────

export interface ImageViewStats {
  today: number;
  total: number;
  average30Days: number;
}

export interface ImageViewHistory {
  date: string;
  count: number;
}

// ────────────────────────────────────────────────────────────
// Sync Management Types
// ────────────────────────────────────────────────────────────

export interface SyncResult {
  success: boolean;
  /** Shown as is, e.g. that a background import started. */
  message?: string;
  setsProcessed?: number;
  setsCreated?: number;
  setsUpdated?: number;
  cardsProcessed?: number;
  cardsCreated?: number;
  cardsUpdated?: number;
  translationsCreated?: number;
  imagesRegistered?: number;
  errors?: string[];
  duration?: number;
}

export interface SetVerification {
  setId: string;
  localCount: number;
  officialCount: number | null;
  isComplete: boolean;
  missingCardIds: string[];
  source: 'tcgdex' | 'pokemontcg' | 'unknown';
}

export interface EnrichResult {
  cardsChecked: number;
  cardsEnriched: number;
  cardsCreated: number;
  translationsCreated: number;
  imagesRegistered: number;
  errors: string[];
}

export interface CopyEnToFrResult {
  cardsChecked: number;
  translationsCopied: number;
  imagesCopied: number;
  errors: string[];
}

export type CardIssueType = 'missingFr' | 'missingEn' | 'copiedEnToFr' | 'noImage' | 'all';

export interface CardWithIssues {
  cardId: string;
  localId: string;
  setId: string;
  nameFr?: string;
  nameEn?: string;
  rarity?: string;
  imageUrl?: string;
  issues: string[];
  frSource?: string;
  enSource?: string;
}

export interface CardsWithIssuesResponse {
  cards: CardWithIssues[];
  total: number;
  stats: {
    missingFr: number;
    missingEn: number;
    copiedEnToFr: number;
    noImage: number;
  };
}

export interface SetListItem {
  id: string;
  tcgdexId: string;
  name: string;
  nameFr?: string;
  nameEn?: string;
  logo?: string;
  symbol?: string;
  cardCount: number;
  localCardCount: number;
  isComplete: boolean;
  releaseDate?: string;
}

export interface AvailableSet {
  id: string;
  name: string;
  logo?: string;
  symbol?: string;
  totalCards: number;
  localCards: number;
  isSynced: boolean;
  isComplete: boolean;
  releaseDate?: string;
}

export interface CardInSet {
  card: {
    _id: string;
    setId: string;
    localId: string;
    rarity?: string;
    hp?: number;
    types?: string[];
    isComplete?: boolean;
  };
  translation: {
    cardId: string;
    lang: string;
    name: string;
    description?: string;
  } | null;
  image: {
    cardId: string;
    lang: string;
    quality: string;
    status: string;
    localPath?: string;
    externalUrl?: string;
  } | null;
}

export interface SetWithCards {
  set: {
    _id: string;
    tcgdexId: string;
    logo?: string;
    symbol?: string;
    cardCount?: { total?: number; official?: number };
    releaseDate?: string;
    isComplete?: boolean;
  };
  setTranslation: {
    setId: string;
    lang: string;
    name: string;
  } | null;
  cards: CardInSet[];
  stats: {
    totalCards: number;
    withTranslation: number;
    withImage: number;
    downloadedImages: number;
  };
}

// ────────────────────────────────────────────────────────────
// Image Storage Types
// ────────────────────────────────────────────────────────────

export interface DownloadStats {
  total: number;
  downloaded: number;
  failed: number;
  skipped: number;
  duration: number;
}

export interface ImageJobStatus {
  enabled: boolean;
  running: boolean;
  batchSize: number;
  intervalMinutes: number;
  lastRunAt?: string;
  nextRunAt?: string;
  lastRun?: DownloadStats;
}

export interface UserWithStats {
  _id: string;
  email: string;
  pseudo: string;
  role: string;
  friendCode?: string;
  createdAt: string;
  updatedAt: string;
  cardsCount: number;
  totalValue: number;
}

export interface UpdateCardData {
  quantity?: number;
  booster?: boolean;
  language?: 'fr' | 'en' | 'ja';
  purchasePrice?: number;
  purchaseDate?: string;
  graded?: boolean;
  grading?: {
    company?: string;
    grade?: string;
    certificationNumber?: string;
  };
  notes?: string;
  variants?: Array<{
    purchasePrice?: number;
    purchaseDate?: string;
    booster?: boolean;
    graded?: boolean;
    grading?: {
      company?: string;
      grade?: string;
      certificationNumber?: string;
    };
    notes?: string;
    language?: string;
  }>;
  isFavorite?: boolean;
}

export interface UserCardDetails {
  _id: string;
  cardId: string;
  ownerId: string;
  quantity?: number;
  booster?: boolean;
  language?: 'fr' | 'en' | 'ja';
  purchasePrice?: number;
  purchaseDate?: string;
  graded?: boolean;
  grading?: {
    company?: string;
    grade?: string;
    certificationNumber?: string;
  };
  notes?: string;
  variants?: Array<{
    purchasePrice?: number;
    purchaseDate?: string;
    booster?: boolean;
    graded?: boolean;
    grading?: {
      company?: string;
      grade?: string;
      certificationNumber?: string;
    };
    notes?: string;
    language?: string;
  }>;
  isFavorite?: boolean;
  cardSnapshot?: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface UserCard {
  _id: string;
  cardId: string;
  name: string;
  setName?: string;
  imageUrl?: string;
  quantity: number;
  purchasePrice?: number;
  purchaseDate?: string;
  graded?: boolean;
  grading?: {
    company?: string;
    grade?: string;
    certificationNumber?: string;
  };
  notes?: string;
  language?: string;
}

export interface UserDetails {
  user: {
    _id: string;
    email: string;
    pseudo: string;
    role: string;
    createdAt: string;
    isActive: boolean;
    updatedAt: string;
  };
  cards: UserCard[];
  stats: {
    cardsCount: number;
    totalValue: number;
  };
}

export interface ActivityLog {
  _id: string;
  userId: string;
  userEmail: string;
  userPseudo?: string;
  userRole?: string;
  type: string;
  category?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  updatedAt: string;
}

window.addEventListener('pagehide', () => sendImageViewsBatch(true));

const trackImageViews = (count: number) => {
  if (count <= 0) return;
  imageViewsToTrack += count;

  if (!trackingTimeout) {
    trackingTimeout = window.setTimeout(() => sendImageViewsBatch(), 10000);
  }
};

export const adminService = {
  async getGlobalStats(): Promise<GlobalStats> {
    const response = await api.get<GlobalStats>('/admin/stats');
    return response.data;
  },

  async getTopCards(limit = 10): Promise<TopCard[]> {
    const response = await api.get<TopCard[]>(`/admin/stats/top-cards?limit=${limit}`);
    return response.data;
  },

  async getTopUsers(limit = 10): Promise<TopUser[]> {
    const response = await api.get<TopUser[]>(`/admin/stats/top-users?limit=${limit}`);
    return response.data;
  },

  async getChartsData(days = 30): Promise<ChartData> {
    const response = await api.get<ChartData>(`/admin/stats/charts?days=${days}`);
    return response.data;
  },

  async getSetDistribution(limit = 20): Promise<SetDistribution[]> {
    const response = await api.get<SetDistribution[]>(`/admin/stats/sets?limit=${limit}`);
    return response.data;
  },

  async getAllUsers(): Promise<UserWithStats[]> {
    const response = await api.get<UserWithStats[]>('/admin/users');
    return response.data;
  },

  async getUserDetails(userId: string): Promise<UserDetails> {
    const response = await api.get<UserDetails>(`/admin/users/${userId}`);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  async deleteUserCard(userId: string, cardId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}/cards/${cardId}`);
  },

  async getActivityLogs(params?: {
    limit?: number;
    skip?: number;
    userId?: string;
    type?: string;
    category?: string;
    excludeAdmins?: boolean;
    excludeUserEmail?: string;
  }): Promise<{ logs: ActivityLog[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.skip) query.append('skip', params.skip.toString());
    if (params?.userId) query.append('userId', params.userId);
    if (params?.type) query.append('type', params.type);
    if (params?.category) query.append('category', params.category);
    if (params?.excludeAdmins) query.append('excludeAdmins', 'true');
    if (params?.excludeUserEmail) query.append('excludeUserEmail', params.excludeUserEmail);

    const response = await api.get<{ logs: ActivityLog[]; total: number }>(
      `/admin/logs?${query.toString()}`
    );
    return response.data;
  },

  async getUnreadLogCounts(): Promise<Record<string, number>> {
    const response = await api.get<Record<string, number>>('/admin/logs/unread-counts');
    return response.data;
  },

  async markCategoryAsViewed(category: string): Promise<void> {
    await api.post(`/admin/logs/mark-viewed/${category}`);
  },

  async getLoginStats(days = 30): Promise<{ date: string; count: number }[]> {
    const response = await api.get<{ date: string; count: number }[]>(
      `/admin/stats/logins?days=${days}`
    );
    return response.data;
  },

  async getUniqueVisitorsStats(days = 30): Promise<{ date: string; count: number }[]> {
    const response = await api.get<{ date: string; count: number }[]>(
      `/admin/stats/unique-visitors?days=${days}`
    );
    return response.data;
  },

  async getTotalValueEvolution(days = 30): Promise<{ date: string; value: number }[]> {
    const response = await api.get<{ date: string; value: number }[]>(
      `/admin/stats/total-value-evolution?days=${days}`
    );
    return response.data;
  },

  async getTotalValueEvolutionByPurchaseDate(
    days = 30
  ): Promise<{ date: string; value: number }[]> {
    const response = await api.get<{ date: string; value: number }[]>(
      `/admin/stats/total-value-evolution-purchase?days=${days}`
    );
    return response.data;
  },

  async updateUser(
    userId: string,
    updates: { pseudo?: string; isActive?: boolean }
  ): Promise<void> {
    await api.patch(`/admin/users/${userId}`, updates);
  },

  async getUserCard(userId: string, cardId: string): Promise<UserCardDetails> {
    const response = await api.get(`/admin/users/${userId}/cards/${cardId}`);
    return response.data;
  },

  async updateUserCard(userId: string, cardId: string, updates: UpdateCardData): Promise<void> {
    await api.patch(`/admin/users/${userId}/cards/${cardId}`, updates);
  },

  async getTopExpensiveCards(limit = 10): Promise<TopExpensiveCard[]> {
    const response = await api.get<TopExpensiveCard[]>(
      `/admin/stats/top-expensive-cards?limit=${limit}`
    );
    return response.data;
  },

  async getTopExpensiveGradedCards(limit = 10): Promise<TopExpensiveGradedCard[]> {
    const response = await api.get<TopExpensiveGradedCard[]>(
      `/admin/stats/top-expensive-graded-cards?limit=${limit}`
    );
    return response.data;
  },

  // ────────────────────────────────────────────────────────────
  // API Request Tracking Methods
  // ────────────────────────────────────────────────────────────

  async getImageViewStats(): Promise<ImageViewStats> {
    const response = await api.get<ImageViewStats>('/admin/stats/image-views');
    return response.data;
  },

  async getImageViewHistory(days = 30): Promise<ImageViewHistory[]> {
    const response = await api.get<ImageViewHistory[]>('/admin/stats/image-views/history', {
      params: { days },
    });
    return response.data;
  },
  trackImageViews,

  // ────────────────────────────────────────────────────────────
  // Sync Management
  // ────────────────────────────────────────────────────────────

  /** Catalog upgrade running on the API (search names, image URLs, Pokédex numbers). */
  async getSyncTasks(): Promise<SyncTasks> {
    const response = await api.get<SyncTasks>('/admin/sync/tasks');
    return response.data;
  },

  async getCatalogOverview(): Promise<CatalogOverview> {
    const response = await api.get<CatalogOverview>('/admin/sync/overview');
    return response.data;
  },

  /** Refreshes the market prices of owned cards in background; force: every card. */
  async refreshPrices(force = false): Promise<PriceRefreshProgress & { started: boolean }> {
    const response = await api.post<PriceRefreshProgress & { started: boolean }>(
      '/admin/prices/refresh',
      {},
      { params: force ? { force: 'true' } : undefined }
    );
    return response.data;
  },

  async startBackfill(force = false): Promise<BackfillProgress & { started: boolean }> {
    const response = await api.post<BackfillProgress & { started: boolean }>(
      '/admin/sync/backfill',
      {},
      { params: force ? { force: 'true' } : undefined }
    );
    return response.data;
  },

  /** Starts a background import of every set, or of one set; follow it with getSyncTasks. */
  async startImport(
    langs: string[] = ['fr', 'en'],
    setId?: string
  ): Promise<ImportProgress & { started: boolean }> {
    const response = await api.post<ImportProgress & { started: boolean }>(
      setId ? `/admin/sync/set/${encodeURIComponent(setId)}` : '/admin/sync/all',
      {},
      { params: { langs: langs.join(',') } }
    );
    return response.data;
  },

  /** Resolves once the running import is over. */
  async waitForImport(pollMs = 2000): Promise<ImportProgress> {
    for (;;) {
      const { import: progress } = await adminService.getSyncTasks();
      if (!progress.running) return progress;
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  },

  async syncCard(cardId: string, langs: string[] = ['fr', 'en']): Promise<SyncResult> {
    const response = await api.post<SyncResult>(
      `/admin/sync/card/${cardId}`,
      {},
      {
        params: { langs: langs.join(',') },
        timeout: 60000, // 1 minute pour une carte
      }
    );
    const data = response.data;
    return {
      ...data,
      success: !data.errors || data.errors.length === 0,
    };
  },

  async verifySetCompleteness(setId: string): Promise<SetVerification> {
    const response = await api.get<SetVerification>(`/admin/sync/verify/${setId}`);
    return response.data;
  },

  async completeMissingCards(setId: string, langs: string[] = ['fr', 'en']): Promise<SyncResult> {
    const response = await api.post<SyncResult>(
      `/admin/sync/complete/${setId}`,
      {},
      {
        params: { langs: langs.join(',') },
        timeout: 300000, // 5 minutes
      }
    );
    const data = response.data;
    return {
      ...data,
      success: !data.errors || data.errors.length === 0,
    };
  },

  async enrichIncompleteCards(
    setId: string,
    langs: string[] = ['fr', 'en']
  ): Promise<EnrichResult> {
    const response = await api.post<EnrichResult>(
      `/admin/sync/enrich/${setId}`,
      {},
      {
        params: { langs: langs.join(',') },
        timeout: 300000, // 5 minutes
      }
    );
    return response.data;
  },

  async copyEnToFr(setId: string): Promise<CopyEnToFrResult> {
    const response = await api.post<CopyEnToFrResult>(
      `/admin/sync/copy-en-to-fr/${setId}`,
      {},
      {
        timeout: 60000, // 1 minute (operation locale rapide)
      }
    );
    return response.data;
  },

  async getCardsWithIssues(
    options: {
      setId?: string;
      issueType?: CardIssueType;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<CardsWithIssuesResponse> {
    const params = new URLSearchParams();
    if (options.setId) params.append('setId', options.setId);
    if (options.issueType) params.append('issueType', options.issueType);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await api.get<CardsWithIssuesResponse>(
      `/admin/sync/cards-with-issues?${params.toString()}`
    );
    return response.data;
  },

  async updateCardDataExtended(
    cardId: string,
    updates: { nameFr?: string; nameEn?: string; rarity?: string; hp?: number; imageUrl?: string }
  ): Promise<{ success: boolean }> {
    const response = await api.patch<{ success: boolean }>(
      `/admin/sync/cards/${cardId}/extended`,
      updates
    );
    return response.data;
  },

  async listAllSets(lang: string = 'fr'): Promise<SetListItem[]> {
    const response = await api.get<SetListItem[]>('/admin/sync/sets', {
      params: { lang },
    });
    return response.data;
  },

  async listAvailableSets(): Promise<AvailableSet[]> {
    const response = await api.get<AvailableSet[]>('/admin/sync/available-sets');
    return response.data;
  },

  async getSetWithCards(setId: string, lang: string = 'fr'): Promise<SetWithCards> {
    const response = await api.get<SetWithCards>(`/admin/sync/sets/${setId}`, {
      params: { lang },
    });
    return response.data;
  },

  async updateSetData(
    setId: string,
    updates: { nameFr?: string; nameEn?: string; logo?: string; symbol?: string }
  ): Promise<{ success: boolean }> {
    const response = await api.patch<{ success: boolean }>(`/admin/sync/sets/${setId}`, updates);
    return response.data;
  },

  // ────────────────────────────────────────────────────────────
  // Image Storage Management
  // ────────────────────────────────────────────────────────────

  async downloadSetImages(
    setId: string,
    lang = 'fr',
    quality: 'high' | 'low' = 'high'
  ): Promise<DownloadStats> {
    const response = await api.post<DownloadStats>(
      `/admin/images/download-set/${setId}`,
      {},
      {
        params: { lang, quality },
      }
    );
    return response.data;
  },

  async cleanupOrphanedImages(): Promise<{ deleted: number; freedBytes: number; errors?: number }> {
    const response = await api.post<{ deleted: number; freedBytes: number; errors?: number }>(
      '/admin/images/cleanup'
    );
    return response.data;
  },

  /** Puts failed downloads back in the queue; returns how many. */
  async retryFailedImages(): Promise<{ reset: number }> {
    const response = await api.post<{ reset: number }>('/admin/images/retry-failed');
    return response.data;
  },

  async enableImageJob(): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/admin/images/job/enable'
    );
    return response.data;
  },

  async disableImageJob(): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/admin/images/job/disable'
    );
    return response.data;
  },

  async runImageJobNow(): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/admin/images/job/run-now'
    );
    return response.data;
  },

  async setImageJobBatchSize(size: number): Promise<{ success: boolean; batchSize: number }> {
    const response = await api.post<{ success: boolean; batchSize: number }>(
      '/admin/images/job/batch-size',
      {},
      { params: { size } }
    );
    return response.data;
  },

  async setImageJobInterval(
    minutes: number
  ): Promise<{ success: boolean; intervalMinutes: number }> {
    const response = await api.post<{ success: boolean; intervalMinutes: number }>(
      '/admin/images/job/interval',
      {},
      { params: { minutes } }
    );
    return response.data;
  },

  async downloadCardImage(
    cardId: string,
    lang: string,
    quality: 'high' | 'low' = 'high'
  ): Promise<{
    success: boolean;
    localPath?: string;
    fileSize?: number;
    error?: string;
    alreadyExists?: boolean;
  }> {
    const response = await api.post<{
      success: boolean;
      localPath?: string;
      fileSize?: number;
      error?: string;
      alreadyExists?: boolean;
    }>(
      `/admin/images/download-card/${cardId}/${lang}`,
      {},
      {
        params: { quality },
        timeout: 60000, // 1 minute pour le téléchargement
      }
    );
    return response.data;
  },
};
